/**
 * Reusable drag-and-drop sortable list using @dnd-kit.
 * Renders children via renderItem(item, isDragging) so callers control the row UI.
 *
 * Usage:
 *   <SortableList
 *     items={items}
 *     getId={(it) => it.item_id}
 *     onReorder={(newItems) => save(newItems)}
 *     renderItem={(it, isDragging, dragHandleProps) => (<MyRow ... />)}
 *   />
 *
 * The drag handle is exposed via `dragHandleProps` — apply it to whatever element
 * (e.g. a GripVertical icon) should initiate the drag.
 */
import { useState } from "react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableRow({ id, children, renderItem, item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-row-${id}`}>
      {renderItem
        ? renderItem(item, isDragging, { ...attributes, ...listeners })
        : children}
    </div>
  );
}

export default function SortableList({
  items,
  getId,
  onReorder,
  renderItem,
  testId = "sortable-list",
}) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = items.map((it) => String(getId(it)));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder && onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div data-testid={testId}>
          {items.map((it) => (
            <SortableRow
              key={getId(it)}
              id={String(getId(it))}
              item={it}
              renderItem={renderItem}
            />
          ))}
        </div>
      </SortableContext>
      {/* DragOverlay omitted on purpose: the row itself is dimmed via isDragging */}
      {activeId && null}
    </DndContext>
  );
}
